// 牌山（TWall）與洗牌
// 對應 PRD §13.5 CRound State 的 wall 欄位、§14.1 牌山測試項
//
// 設計原則：
// - 純函式：所有 mutation 都產生新物件
// - 隨機性透過注入的 TRng（types.ts）控制，方便測試重現
// - 144 張砌成四面、每面 18 墩；依開門位置旋轉實際抓牌順序
// - 活牌從開門端抓，補花／槓後補牌從尾端抓；海底保留 16 張，每完成一槓再多保留 1 張

import { createFullTileSet, type TTile } from './tile';
import type { TRng, TSeat } from './types';

const WALL_SIDE_COUNT = 4;
const STACKS_PER_SIDE = 18;
const TILES_PER_STACK = 2;
const DEFAULT_DEAD_WALL_COUNT = 16;

export interface IWallOpening {
  readonly openingSeat: TSeat;
  /** 已數完骰值後，下一個開始抓取的墩位（0-based）。 */
  readonly firstStackIndex: number;
  /** 72 墩環狀牌牆的實際起抓位置；有值時優先使用。 */
  readonly wallStartStackIndex?: number;
}

/** 牌山狀態：剩下還沒抓的牌。 */
export type TWall = {
  readonly tiles: readonly TTile[];
  /** 洗牌後砌成的原始四面牌牆；只供規則追蹤，不隨抓牌 mutation。 */
  readonly sides: readonly (readonly (readonly [TTile, TTile])[])[];
  readonly deadWallCount: number;
  readonly completedKongCount: number;
  readonly opening: IWallOpening | null;
};

/**
 * Mulberry32 — 小型 seedable PRNG。
 *
 * 為什麼不用 Math.random？因為要讓單元測試與 replay 可重現。
 * 給定相同 seed，每次產生的序列完全一樣。
 *
 * 數學品質對「洗牌」夠用；密碼學請另尋強度更高的演算法。
 */
export const createSeedRng = (seed: number): TRng => {
  // >>> 0 強制轉成 unsigned 32-bit
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Fisher-Yates 洗牌（純函式）。
 *
 * 從尾部往前遍歷，每次與 [0..i] 中隨機一張交換。
 * 統計上各排列出現機率均勻。
 */
export const shuffle = <T>(items: readonly T[], rng: TRng): T[] => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    // 三元交換（TypeScript 解構需要明確型別保證）
    const tmp = arr[i] as T;
    arr[i] = arr[j] as T;
    arr[j] = tmp;
  }
  return arr;
};

/**
 * 建立一個洗好的完整 144 張牌山。
 *
 * 用法：在 CRound.INIT 階段呼叫一次，之後 wall 狀態不再洗牌。
 */
export const createWall = (rng: TRng, opening: IWallOpening | null = null): TWall => {
  const shuffled = shuffle(createFullTileSet(), rng);
  const stacks: Array<readonly [TTile, TTile]> = [];
  for (let index = 0; index < shuffled.length; index += TILES_PER_STACK) {
    stacks.push([shuffled[index]!, shuffled[index + 1]!]);
  }

  const sides = Array.from({ length: WALL_SIDE_COUNT }, (_, sideIndex) =>
    stacks.slice(sideIndex * STACKS_PER_SIDE, (sideIndex + 1) * STACKS_PER_SIDE)
  );

  let orderedStacks = stacks;
  if (opening) {
    if (
      !Number.isInteger(opening.firstStackIndex) ||
      opening.firstStackIndex < 0 ||
      opening.firstStackIndex >= STACKS_PER_SIDE
    ) {
      throw new Error(`Invalid wall opening stack: ${opening.firstStackIndex}`);
    }
    const start =
      opening.wallStartStackIndex ??
      opening.openingSeat * STACKS_PER_SIDE + opening.firstStackIndex;
    if (!Number.isInteger(start) || start < 0 || start >= stacks.length) {
      throw new Error(`Invalid wall start stack: ${start}`);
    }
    orderedStacks = [...stacks.slice(start), ...stacks.slice(0, start)];
  }

  return {
    tiles: orderedStacks.flatMap((stack) => [stack[0], stack[1]]),
    sides,
    deadWallCount: DEFAULT_DEAD_WALL_COUNT,
    completedKongCount: 0,
    opening,
  };
};

/**
 * 從牌山頂抓一張，回傳 [新牌山, 抓到的牌]。
 * 牌山空時回傳 null（流局判定的觸發點）。
 */
export const drawTile = (wall: TWall): readonly [TWall, TTile] | null => {
  if (wall.tiles.length === 0) return null;
  const [tile, ...rest] = wall.tiles;
  return [{ ...wall, tiles: rest }, tile as TTile] as const;
};

/** 從活牌端摸牌；碰到海底保留區即回 null。 */
export const drawLiveTile = (wall: TWall): readonly [TWall, TTile] | null => {
  if (wall.tiles.length <= wall.deadWallCount) return null;
  return drawTile(wall);
};

/** 補花／槓後從牌尾補牌，不改變海底保留數。 */
export const drawReplacementTile = (wall: TWall): readonly [TWall, TTile] | null => {
  if (wall.tiles.length === 0) return null;
  const tile = wall.tiles[wall.tiles.length - 1]!;
  return [{ ...wall, tiles: wall.tiles.slice(0, -1) }, tile] as const;
};

/** 槓完成後海底多保留一張；搶槓成功時不得呼叫。 */
export const recordCompletedKong = (wall: TWall): TWall => ({
  ...wall,
  deadWallCount: wall.deadWallCount + 1,
  completedKongCount: wall.completedKongCount + 1,
});

/** 牌山是否已抽完（流局判定用）。 */
export const isWallEmpty = (wall: TWall): boolean => wall.tiles.length === 0;

/** 牌山剩餘張數（UI 顯示「還剩 N 張」用）。 */
export const wallRemaining = (wall: TWall): number => wall.tiles.length;

export const liveWallRemaining = (wall: TWall): number =>
  Math.max(0, wall.tiles.length - wall.deadWallCount);

export const isLiveWallEmpty = (wall: TWall): boolean => liveWallRemaining(wall) === 0;
